/* English dictionary: debug namespace. See src/i18n/README.md. */

import type { TextSpec } from '../text';

const debug = {
  'debug.index.title': [ 'Ivory Roulette · Debug Pages' ],
  'debug.index.intro': [ 'These pages are <strong>not the game UI</strong>; they are tools for tuning one layer at a time.\n      The game UI is at <code>/</code> (see docs/STATUS.md).' ],
  'debug.index.palette': [ 'Palette' ],
  'debug.index.paletteNote': [ 'The palette is defined in <code>src/render/palette.ts</code>.\n      <strong>SIGNAL</strong> is the fluorescent base color untouched (for glow);\n      <strong>STROKE</strong> is the same hue pulled toward mid-gray (for lines) —\n      on a black background dimming a line makes it disappear, so saturation is reduced rather than brightness.' ],
  'debug.index.pg.background.name': [ 'Background · Scalar field + halftone' ],
  'debug.index.pg.background.desc': [ 'Background only, no card layer. Each slider maps to one uniform' ],
  'debug.index.pg.textures.name': [ 'Intermediate data · FBO / textures' ],
  'debug.index.pg.textures.desc': [ 'Tile every normally invisible intermediate product' ],
  'debug.index.pg.dying.name': [ 'Dying · 45° red slash bands' ],
  'debug.index.pg.dying.desc': [ 'Drawing the exception card: card floats center (never joins the hand) · black halftone shadow · breathing red glow · bands run to both sides' ],
  'debug.index.pg.dyingBg.name': [ 'Background during dying · repro rig' ],
  'debug.index.pg.dyingBg.desc': [ 'Background only (scalar field + halftone + crack mask): the three dying events, every parameter, coordinate diagnostic filter, pitch readout and snapshot comparison' ],
  'debug.index.pg.impact.name': [ 'Dying · Impact' ],
  'debug.index.pg.impact.desc': [ 'The hit when the exception card fills the screen: freeze frame · white flash · invert · shake (trauma) · zoom · desaturate/chromatic aberration · vignette · edge pulse — one slider per knob, Space = land a hit' ],
  'debug.index.pg.face.name': [ 'Card faces · Tuning' ],
  'debug.index.pg.face.desc': [ 'Lay out all 13 cards; one slider per tunable parameter (edits values in tune.ts)' ],
  'debug.index.pg.art.name': [ 'Art assets · Card back + title logo' ],
  'debug.index.pg.art.desc': [ 'Two non-card assets sharing the same spec pipeline as card faces' ],
  'debug.index.pg.icon.name': [ 'Small logo · favicon' ],
  'debug.index.pg.icon.desc': [ 'Transparent 1:1 "A" across a full set of sizes; scripts/icons.mjs grabs images from this page' ],
  'debug.index.pg.cards.name': [ 'Card rendering' ],
  'debug.index.pg.cards.desc': [ 'Fake-perspective parameter reference table' ],
  'debug.index.pg.hand.name': [ 'Hand · Feel' ],
  'debug.index.pg.hand.desc': [ 'Idle sway · hover faces the mouse · press · whole fan lift and neighbor avoidance · movement-direction tilt and speed limit' ],
  'debug.index.pg.full.name': [ 'Everything combined' ],
  'debug.index.pg.full.desc': [ 'Background + dying + cards + holes + outer glow' ],
  'debug.index.pg.ui.name': [ 'UI · Components and state vocabulary' ],
  'debug.index.pg.ui.desc': [ 'Four variants × four states side by side — the "hover only adds ink" rule can only be verified in parallel' ],
  'debug.index.pg.bgm.name': [ 'BGM · Dynamic music + beat sync' ],
  'debug.index.pg.bgm.desc': [ 'BgmDirector drives the zenith score: the pulse circle hits once every 2 bars, cue changes align to bar boundaries, and the isDying rising edge rewinds to the start' ],
  'debug.shared.back': [ '← Debug index' ],
  'debug.ui.title': [ 'UI · Components and state vocabulary' ],
  'debug.ui.subtitle': [ 'The table from docs/ui-design.md, laid out' ],
  'debug.ui.statesTitle': [ 'State vocabulary · every variant × four states' ],
  'debug.ui.lift': [ 'Lift ', { type: 'param', name: 'px' }, 'px' ],
  'debug.ui.liftNote': [ 'hover lifts ', { type: 'param', name: 'lift' }, 'px (whole block, including text) · press sinks ', { type: 'param', name: 'press' }, 'px · ', { type: 'param', name: 'range' }, 'ms transition with hard stop, no overshoot' ],
  'debug.ui.barsTitle': [ 'Signal bars · the .uibar from the sample page' ],
  'debug.ui.barWait': [ 'Awaiting response · someone has not spoken' ],
  'debug.ui.barDraw': [ 'Lethal response window · someone drew the exception' ],
  'debug.ui.hazardNote': [ 'Hazard stripes have only two sources: the white card bottom edge + here. Used a second time they become a cliché — no longer a threat.' ],
  'debug.ui.chromeTitle': [ 'Chrome' ],
  'debug.ui.notice': [ { type: 'param', name: 'tone' }, ' · a full-width notice' ],
  'debug.ui.readout': [ 'ROM 000', { type: 'param', name: 'n' }, ' · a machine-readable line (monospace, low contrast, indexed)' ],
  'debug.ui.operatorLabel': [ 'Host after costs' ],
  'debug.ui.operatorValue': [ 'OPERATOR-0000 (fastest network)' ],
  'debug.ui.aliveTitle': [ 'Live controls · move the mouse over to see hover' ],
  'debug.ui.btn.primary': [ 'Primary action' ],
  'debug.ui.btn.secondary': [ 'Secondary action' ],
  'debug.ui.btn.quiet': [ 'Quiet entry' ],
  'debug.ui.btn.danger': [ 'Destructive' ],
  'debug.ui.btn.disabled': [ 'Disabled' ],
  'debug.ui.toggleOn': [ 'Toggle (on)' ],
  'debug.ui.toggleOff': [ 'Toggle (off)' ],
  'debug.ui.toggleDisabled': [ 'Toggle (disabled)' ],
  'debug.ui.stepper': [ 'Stepper' ],
  'debug.ui.slider': [ 'Slider' ],
  'debug.ui.panel': [ 'Panel' ],
  'debug.ui.section': [ 'Section title / SECTION' ],
  'debug.ui.panelNote': [ 'Panel height is computed by fitContent; callers report only content height' ],
  'debug.ui.chromeHeader': [ 'UI Sample · KIT' ],
  'debug.ui.pressRed': [ 'PRESS.red · the ink shared by hazard stripes and the white card bottom edge' ],
  'debug.cards.title': [ 'Card rendering · Fake perspective' ],
  'debug.cards.subtitle': [ 'Each row changes one parameter' ],
  'debug.full.title': [ 'Everything combined' ],
  'debug.full.subtitle': [ 'Background holes + dying bands + outer glow + cards' ],
  'debug.full.glow': [ 'Outer glow' ],
  'debug.full.radius': [ 'Spread radius' ],
  'debug.full.strength': [ 'Strength' ],
  'debug.full.hideCards': [ 'Hide cards' ],
  'debug.full.begin': [ 'Enter dying' ],
  'debug.full.end': [ 'End' ],
  'debug.full.info': [ 'Card face FBOs: ', { type: 'param', name: 'faces' }, ' · field disturbances: ', { type: 'param', name: 'count' } ],
  'debug.icon.title': [ 'Small logo · favicon' ],
  'debug.icon.subtitle': [ 'Transparent · 1:1 · letter A only' ],
  'debug.icon.lightTab': [ 'Light tab bar' ],
  'debug.icon.darkTab': [ 'Dark tab bar' ],
  'debug.icon.caption': [ 'Small logo · 1:1 · a red wedge · transparent' ],
  'debug.icon.caption2': [ 'The top row is the pixels actually seen in the browser; the bottom row is the enlarged shape' ],
  'debug.icon.maskable': [ 'maskable-192 · yellow circle = Android safe zone' ],
  'debug.icon.sizes': [ { type: 'param', name: 'count' }, ' sizes + favicon.ico (contains ', { type: 'param', name: 'list' }, ') · node scripts/icons.mjs' ],
  'debug.textures.title': [ 'Intermediate data · FBO / textures' ],
  'debug.textures.subtitle': [ 'Tile cell by cell to locate which stage the problem is in' ],
  'debug.textures.noLethal': [ 'No lethal card in the base pack' ],
  'debug.textures.footprint': [ 'Card footprint mask (footprint.mask)' ],
  'debug.textures.background': [ 'Background · scalar field + halftone (result after both filters)' ],
  'debug.textures.face': [ 'Card face FBO · with bleed (card height ', { type: 'param', name: 'height' }, ')' ],
  'debug.textures.dyingField': [ 'Dying · the glow (card-shaped silhouette + wide blur)' ],
  'debug.textures.dyingShadow': [ 'Dying · black halftone shadow (= the previous one with a halftone filter, knockout mode)' ],
  'debug.textures.reload': [ 'Read again' ],
  'debug.art.title': [ 'Art assets · Card back + title logo' ],
  'debug.art.subtitle': [ 'Both share the same spec pipeline as card faces' ],
  'debug.art.backCaption': [ 'Card back · whether a row reads as the same back (zero acid color)' ],
  'debug.art.titleCaption': [ 'Title logo · ACID SAMIZDAT (transparent, over mid-gray)' ],
  'debug.art.backNote': [ 'Card-back center: the same "card" printed twice, the second offset by ', { type: 'param', name: 'offset' }, ' (two misaligned plates)' ],
  'debug.art.titleNote': [ 'Design space ', { type: 'param', name: 'w' }, '×', { type: 'param', name: 'h' }, ' · both lines justified to ', { type: 'param', name: 'measure' }, ' (measured, not guessed)' ],
  'debug.art.weave': [ 'Card back · weave (line screen × dot screen = moiré)' ],
  'debug.art.sigil': [ 'Card back · center mark + corner marks' ],
  'debug.art.titlePanel': [ 'Title logo' ],
  'debug.art.wedge': [ 'Red wedge (shared by logo / favicon)' ],
  'debug.art.dump': [ 'Export parameters (print to console)' ],
  'debug.impact.title': [ 'Dying · Impact' ],
  'debug.impact.subtitle': [ 'Freeze / white flash / invert / shake / zoom / desaturate·chromatic aberration / vignette / edge pulse — see docs/dying-impact.md' ],
  'debug.impact.style': [ 'ImpactStyle (edits memory; copy back after export)' ],
  'debug.impact.replay': [ 'Replay' ],
  'debug.impact.land': [ 'Landing kick' ],
  'debug.impact.hit': [ '★ Main hit' ],
  'debug.impact.riser': [ 'Riser only' ],
  'debug.impact.burst': [ 'Burst particles' ],
  'debug.impact.bar': [ 'Edge pulse' ],
  'debug.impact.reset': [ 'Reset' ],
  'debug.impact.auto': [ 'Auto-hit every 2.5s' ],
  'debug.impact.beatAlign': [ 'Align main hit to the next bar (B4)' ],
  'debug.impact.dyingOn': [ 'Dying layer on' ],
  'debug.impact.glitchOn': [ 'Enable glitch filter' ],
  'debug.impact.glitch': [ 'Glitch filter' ],
  'debug.impact.strength': [ 'Strength' ],
  'debug.impact.readout': [ 'Readout' ],
  'debug.impact.export': [ 'Export' ],
  'debug.impact.dump': [ 'Export parameters' ],
  'debug.bg.title': [ 'Background · Scalar field + halftone' ],
  'debug.bg.subtitle': [ 'No card layer' ],
  'debug.bg.subtitleScale': [ 'Render scale ', { type: 'param', name: 'scale' }, ' (?scale=)' ],
  'debug.bg.field': [ 'Scalar field' ],
  'debug.bg.baseAmp': [ 'Initial state · brightness gain' ],
  'debug.bg.baseBias': [ 'Initial state · bias' ],
  'debug.bg.baseScale': [ 'Initial state · spatial frequency' ],
  'debug.bg.baseSpeed': [ 'Initial state · time scale' ],
  'debug.bg.windowLow': [ 'Window low' ],
  'debug.bg.windowHigh': [ 'Window high' ],
  'debug.bg.halftone': [ 'Halftone' ],
  'debug.bg.dotPitch': [ 'Dot pitch' ],
  'debug.bg.dotAngle': [ 'Screen angle' ],
  'debug.bg.contrast': [ 'Dot contrast (thickness after thresholding)' ],
  'debug.bg.dotShape': [ 'Dot shape (0 circle 1 square 2 diamond 3 cross 4 line)' ],
  'debug.bg.stages': [ 'Stages' ],
  'debug.bg.stageField': [ 'Scalar field' ],
  'debug.bg.stageHalftone': [ 'Halftone' ],
  'debug.bg.pause': [ 'Pause time' ],
  'debug.bg.spawnRipple': [ 'Ripple' ],
  'debug.bg.spawnSwirl': [ 'Swirl' ],
  'debug.bg.spawnPulse': [ 'Pulse' ],
  'debug.bg.spawnWave': [ 'Wave' ],
  'debug.bg.clear': [ 'Clear' ],
  'debug.bg.barAccent': [ '▸ Bar pulse (accent)' ],
  'debug.bg.barWeak': [ '▸ Bar pulse (weak)' ],
  'debug.bg.infoScale': [ 'Scale ', { type: 'param', name: 'scale' }, ' (', { type: 'param', name: 'mode' }, ')' ],
  'debug.bg.hardEdge': [ ' · 1-bit hard edge' ],
  'debug.bg.softEdge': [ ' · soft edge' ],
  'debug.bg.infoField': [ 'Field disturbances: ', { type: 'param', name: 'count' }, ' · time ', { type: 'param', name: 'time' }, 's' ],
  'debug.bg.readout': [ 'Grayscale readout min ', { type: 'param', name: 'min' }, ' · mean ', { type: 'param', name: 'mean' }, ' · max ', { type: 'param', name: 'max' } ],
  'debug.bg.flat': [ '  ← field is flat' ],
  'debug.face.title': [ 'Card faces · Tuning' ],
  'debug.face.subtitle': [ 'All numbers live in src/render/card/art/tune.ts' ],
  'debug.face.heading': [ 'Card face tuning · all 13 laid out on one table' ],
  'debug.face.warm': [ 'Card height ', { type: 'param', name: 'height' }, ' · prewarmed ', { type: 'param', name: 'count' }, ' cards in ', { type: 'param', name: 'ms' }, 'ms (', { type: 'param', name: 'bakes' }, ' bakes) · no growth for the rest of the game' ],
  'debug.face.cache': [ { type: 'param', name: 'cards' }, ' unique cards · face FBOs ', { type: 'param', name: 'fbo' }, ' · ', { type: 'param', name: 'bakes' }, ' bakes / ', { type: 'param', name: 'sprites' }, ' placements — reuse is working' ],
  'debug.face.bleed': [ 'Bleed (the ring outside the card)' ],
  'debug.face.padFade': [ 'padFade fade (off = hard-crop control)' ],
  'debug.face.hand': [ 'Hand-drawn' ],
  'debug.face.jitter': [ 'Jitter HAND.jitter' ],
  'debug.face.press': [ 'Pressure HAND.press' ],
  'debug.face.print': [ 'Print (halftone)' ],
  'debug.face.printPitch': [ 'Pitch PRINT.pitch' ],
  'debug.face.printAngle': [ 'Screen angle PRINT.angle' ],
  'debug.face.printCoverage': [ 'Coverage PRINT.coverage' ],
  'debug.face.hazard': [ 'Hazard stripes (exception bottom + future dying bands)' ],
  'debug.face.hazardPitch': [ 'Pitch hazard.pitch' ],
  'debug.face.hazardAngle': [ 'Angle hazard.angle' ],
  'debug.face.hazardDuty': [ 'Duty hazard.duty' ],
  'debug.face.exception': [ 'Exception · white card (the only one)' ],
  'debug.face.dump': [ 'Export parameters (print to console)' ],
  'debug.dying.title': [ 'Dying · 45° red slash bands' ],
  'debug.dying.subtitle': [ 'Drawing the exception card: card floats center · black halftone shadow · breathing red glow · bands run to both sides' ],
  'debug.dying.noCard': [ 'No ', { type: 'param', name: 'local' }, ' in the base pack' ],
  'debug.dying.noLethal': [ 'No lethal card in the base pack' ],
  'debug.dying.trigger': [ 'Trigger' ],
  'debug.dying.begin': [ 'Enter dying begin()' ],
  'debug.dying.end': [ 'End end()' ],
  'debug.dying.pulse': [ 'Flash pulse()' ],
  'debug.dying.barPulse': [ '▸ Bar accent barPulse(1)' ],
  'debug.dying.crackGrow': [ '▸ Regrow cracks cracks.begin()' ],
  'debug.dying.crackClose': [ 'Close cracks cracks.end()' ],
  'debug.dying.override': [ 'Intensity override (-1 = off)' ],
  'debug.dying.layers': [ 'Layer switches' ],
  'debug.dying.layerBg': [ 'Background' ],
  'debug.dying.layerBand': [ '① Warning bands' ],
  'debug.dying.layerGlow': [ '② Red outer glow' ],
  'debug.dying.layerHalo': [ '③ Black halftone shadow' ],
  'debug.dying.layerHero': [ '④ Floating exception card' ],
  'debug.dying.layerFootprint': [ 'Card footprint outer glow' ],
  'debug.dying.layerCrack': [ 'Crack mask (the layer that zeroes the field)' ],
  'debug.dying.layerCrackView': [ 'Crack mask · shown directly (for shaping)' ],
  'debug.dying.bandHeight': [ 'Height' ],
  'debug.dying.bandPitch': [ 'Pitch' ],
  'debug.dying.bandDuty': [ 'Duty' ],
  'debug.dying.bandSpeed': [ 'Speed (phase/s)' ],
  'debug.dying.bandMirror': [ 'bandMirror · mirror right half (off = translate, same slash direction)' ],
  'debug.dying.glowPanel': [ '② The glow (shared by red glow + black shadow)' ],
  'debug.dying.silhouetteGrow': [ 'Silhouette larger than card (per side)' ],
  'debug.dying.glowBlur': [ 'Blur radius (= shadow size)' ],
  'debug.dying.glowAlphaMin': [ 'Breath min' ],
  'debug.dying.glowAlphaMax': [ 'Breath max' ],
  'debug.dying.breathPeriod': [ 'Breath period (s)' ],
  'debug.dying.crackPanel': [ '④ Crack mask' ],
  'debug.dying.crackLineWidth': [ 'Line width (width of the zeroed strip)' ],
  'debug.dying.crackArms': [ 'Arms' ],
  'debug.dying.crackGrowSeconds': [ 'Full-growth time (s)' ],
  'debug.dying.crackStagger': [ 'Stagger per arm (s)' ],
  'debug.dying.shadowPanel': [ '③ Black halftone shadow (= ② with a halftone filter)' ],
  'debug.dying.shadowPitch': [ 'Dot pitch' ],
  'debug.dying.shadowAngle': [ 'Screen angle (rad)' ],
  'debug.dying.shadowContrast': [ 'Dot contrast (higher = dots more separated)' ],
  'debug.dying.shadowHint': [ '⚠️ Offset the shadow halftone from the background one (11px / 45°); otherwise the two screens moiré. Once happy, copy the numbers back to src/render/dying/layer.ts.' ],
  'debug.dyingBg.title': [ 'Background during dying · repro rig' ],
  'debug.dyingBg.subtitle': [ 'Background only: scalar field + halftone + crack mask. No cards, no dying layer, no glitch filter' ],
  'debug.dyingBg.events': [ 'Events (the three things that really happen during dying)' ],
  'debug.dyingBg.begin': [ 'Enter dying begin()' ],
  'debug.dyingBg.end': [ 'End end()' ],
  'debug.dyingBg.barAccent': [ '▸ Bar (accent 1.0)' ],
  'debug.dyingBg.barWeak': [ '▸ Bar (weak 0.62)' ],
  'debug.dyingBg.autoBar': [ 'Auto Bar (fires per bar, simulates BGM)' ],
  'debug.dyingBg.barSeconds': [ 'Bar length (s)' ],
  'debug.dyingBg.autoCycle': [ 'Auto-cycle dying (in 6s → out 3s)' ],
  'debug.dyingBg.glitchPanel': [ 'Glitch filter (attached to world, same as in game)' ],
  'debug.dyingBg.glitchOn': [ 'On (in game only during dying)' ],
  'debug.dyingBg.glitchStrength': [ 'Strength' ],
  'debug.dyingBg.glitchHint': [ '⚠️ A filter\'s intermediate texture is allocated at the **logical size** by default (Pixi Filter.resolution defaults to 1) — on a DPR 2 screen the whole frame is first drawn at 1280×800 and then scaled up 2×.' ],
  'debug.dyingBg.crackPanel': [ 'Cracks (the dying mask; it edits the field)' ],
  'debug.dyingBg.crackGrow': [ 'Regrow cracks.begin()' ],
  'debug.dyingBg.crackClose': [ 'Close cracks.end()' ],
  'debug.dyingBg.crackLineWidth': [ 'Line width' ],
  'debug.dyingBg.crackArms': [ 'Arms' ],
  'debug.dyingBg.crackGrowSeconds': [ 'Full-growth time (s)' ],
  'debug.dyingBg.crackStrength': [ 'Crack strength' ],
  'debug.dyingBg.moodPanel': [ 'Mood (energy → brightness / tint / time scale)' ],
  'debug.dyingBg.brightnessOverride': [ 'Brightness override (-1 = off)' ],
  'debug.dyingBg.timeScale': [ 'Time scale (0 = frozen)' ],
  'debug.dyingBg.fieldPanel': [ 'Scalar field (stage one)' ],
  'debug.dyingBg.baseAmp': [ 'Initial state · brightness gain' ],
  'debug.dyingBg.baseBias': [ 'Initial state · bias' ],
  'debug.dyingBg.baseScale': [ 'Initial state · spatial frequency' ],
  'debug.dyingBg.windowLow': [ 'Window low' ],
  'debug.dyingBg.windowHigh': [ 'Window high' ],
  'debug.dyingBg.fieldClear': [ 'Clear disturbances' ],
  'debug.dyingBg.halftonePanel': [ 'Halftone (stage two)' ],
  'debug.dyingBg.dotPitch': [ 'Dot pitch' ],
  'debug.dyingBg.dotAngle': [ 'Screen angle' ],
  'debug.dyingBg.contrast': [ 'Dot contrast' ],
  'debug.dyingBg.softness': [ 'Edge softness (soft-edge mode only)' ],
  'debug.dyingBg.dotShape': [ 'Dot shape (0 circle 1 square 2 diamond 3 cross 4 line)' ],
  'debug.dyingBg.diagPanel': [ 'Diagnostics (replace the halftone stage)' ],
  'debug.dyingBg.diagModes': [ '0 off 1 field 2 quad 3 canvas 4 grid 5 pixel grid 6 sample coords' ],
  'debug.dyingBg.diagHint': [ '"Sample coords" is the most useful: only if it spans 0..1 do the texture-pool image and the filter area match; if it only reaches 0.5 the image is twice the area — any shader computing uv as screen coords will be off by 2×.' ],
  'debug.dyingBg.statsPanel': [ 'Readout / snapshot comparison' ],
  'debug.dyingBg.read': [ 'Read once' ],
  'debug.dyingBg.snapshot': [ 'Save snapshot (use now as baseline)' ],
  'debug.dyingBg.clearSnapshot': [ 'Clear snapshot' ],
  'debug.dyingBg.dyingYes': [ 'yes' ],
  'debug.dyingBg.dyingNo': [ 'no' ],
  'debug.dyingBg.infoDying': [ 'Dying ', { type: 'param', name: 'state' }, ' · Bar hit ', { type: 'param', name: 'n' }, ' times' ],
  'debug.dyingBg.infoScale': [ 'Scale ', { type: 'param', name: 'scale' }, ' (', { type: 'param', name: 'mode' }, ')' ],
  'debug.dyingBg.infoMood': [ 'Mood brightness ', { type: 'param', name: 'brightness' }, ' flash ', { type: 'param', name: 'flash' } ],
  'debug.dyingBg.infoTime': [ 'Time scale ', { type: 'param', name: 'time' }, ' · disturbances ', { type: 'param', name: 'count' } ],
  'debug.dyingBg.autoBarOn': [ ' · auto Bar on' ],
  'debug.dyingBg.brightnessOverridden': [ ' · brightness overridden' ],
  'debug.dyingBg.notRead': [ '(not read yet)' ],
  'debug.dyingBg.rowCoverage': [ 'Coverage%' ],
  'debug.dyingBg.rowGrayMean': [ 'Top-right gray' ],
  'debug.dyingBg.rowCells': [ 'Cells' ],
  'debug.dyingBg.rowCellPx': [ 'Cell px' ],
  'debug.dyingBg.rowRuns': [ 'Runs' ],
  'debug.dyingBg.rowRunPx': [ 'Run px' ],
  'debug.dyingBg.rowScreen': [ 'Screen' ],
  'debug.dyingBg.rowInnerSize': [ 'Inner size' ],
  'debug.dyingBg.rowInnerScale': [ 'Inner scale' ],
  'debug.dyingBg.rowViewSize': [ 'View measured' ],
  'debug.dyingBg.diffHeader': [ '—— vs snapshot ——' ],
  'debug.dyingBg.rowCoverageDiff': [ 'Coverage' ],
  'debug.bgm.events': [ 'Event stream (audio time / arrival time)' ],
  'debug.bgm.title': [ 'BGM debug console · BgmDirector + zenith score' ],
  'debug.bgm.intro': [ 'The pulse circle fires once per phrase (2 bars); the four dots are each beat. Toggle buttons take effect only at bar boundaries. The audio is a sprite sheet playing only the loop range — see ' ],
  'debug.bgm.period': [ '.' ],
  'debug.bgm.phrase': [ '#', { type: 'param', name: 'n' }, ' (every ', { type: 'param', name: 'bars' }, ' bars)' ],
  'debug.bgm.restart': [ 'Music back to loop start' ],
  'debug.bgm.source': [ 'In use' ],
  'debug.bgm.supported': [ 'Supported' ],
  'debug.bgm.unsupported': [ 'Unsupported' ],
  'debug.bgm.notLoaded': [ '(not loaded yet)' ],
  'debug.bgm.paramFinal': [ 'isFinal → ', { type: 'param', name: 'value' }, ' (takes effect next bar)' ],
  'debug.hand.title': [ 'Hand · Feel' ],
  'debug.hand.subtitle': [ 'Idle sway · hover turns toward the mouse · press · select · drag to reorder · drag out to play' ],
  'debug.hand.played': [ 'Played ', { type: 'param', name: 'cards' } ],
  'debug.hand.shapePanel': [ 'Layout · Auto-derived' ],
  'debug.hand.count': [ 'Count (changing it rebuilds CardView)' ],
  'debug.hand.curl': [ 'curl · desired bend (unit: card height)' ],
  'debug.hand.fill': [ 'fill · max fraction of canvas width' ],
  'debug.hand.shapeNote': [ '⚠️ Arc center x/y, spacing, and roll gain are **not** knobs: they are derived from canvas size + card size + count. Past curl 0.5 the picture stops changing — that is the "edge cards show at least half" rule kicking in.' ],
  'debug.hand.idlePanel': [ '① Idle: slow sway' ],
  'debug.hand.idleRollAmp': [ 'Roll amplitude (deg)' ],
  'debug.hand.idlePitchAmp': [ 'pitch amplitude (deg)' ],
  'debug.hand.idlePitchPhase': [ 'pitch phase offset (90 = clockwise circle)' ],
  'debug.hand.idlePeriod': [ 'Period (s)' ],
  'debug.hand.idlePhaseStep': [ 'Phase offset between neighbors (deg)' ],
  'debug.hand.hoverPanel': [ '② hover' ],
  'debug.hand.hoverScale': [ 'Scale amount' ],
  'debug.hand.hoverLift': [ 'Extra self lift (card height)' ],
  'debug.hand.hoverPitch': [ 'Pitch gain toward mouse (deg)' ],
  'debug.hand.hoverYaw': [ '★ Yaw gain toward mouse (deg)' ],
  'debug.hand.hoverFaceRef': [ 'Above two: one unit = how many card heights' ],
  'debug.hand.pressPanel': [ '③ Press / select' ],
  'debug.hand.pressScale': [ 'Extra press scale' ],
  'debug.hand.pressLift': [ 'Extra press lift (card height)' ],
  'debug.hand.selectLift': [ '★ Select lift (card height)' ],
  'debug.hand.selectScale': [ '★ Select scale amount' ],
  'debug.hand.fanLift': [ 'Whole-hand lift (card height)' ],
  'debug.hand.avoidPanel': [ '④ Neighbor avoidance' ],
  'debug.hand.avoid': [ 'Neighbor offset (card width)' ],
  'debug.hand.avoidFalloff': [ 'Falloff one card over' ],
  'debug.hand.scalePanel': [ '⑤ Size' ],
  'debug.hand.scaleRate': [ 'Rate toward target (1/s)' ],
  'debug.hand.scaleNote': [ '⚠️ Size, like position, only approaches **monotonically** — never overshoots, never wobbles. We tried an under-damped spring ("wobble once or twice"); the review disliked it, so it was removed.' ],
  'debug.hand.followPanel': [ '⑥ Follow' ],
  'debug.hand.followRate': [ 'Position follow rate (1/s)' ],
  'debug.hand.angleRate': [ '★ Angle (yaw/pitch/roll) follow rate' ],
  'debug.hand.maxSpeed': [ 'Max move speed (card heights/s, 0 = unlimited)' ],
  'debug.hand.followNote': [ '⚠️ Angles must be smooth too: if the position slides while the tilt snaps a step, it reads at once as two separate things moving.' ],
  'debug.hand.envPanel': [ '⑦ Envelope' ],
  'debug.hand.riseRate': [ 'hover/press rise rate (1/s)' ],
  'debug.hand.fallRate': [ 'hover/press fall rate (1/s)' ],
  'debug.hand.avoidRiseRate': [ '★ Neighbor avoidance rise rate (1/s)' ],
  'debug.hand.avoidFallRate': [ '★ Neighbor avoidance fall rate (1/s)' ],
  'debug.hand.musicPanel': [ '⑧ Music pulse (Bar event)' ],
  'debug.hand.barPulseScale': [ 'Instant scale amount' ],
  'debug.hand.barPulseRate': [ 'Shrink-back rate (1/s)' ],
  'debug.hand.musicNote': [ '⚠️ Scale-up is **hard-on** (no smoothing); only the shrink-back eases out. In the main UI it is driven by BgmDirector Bar events (`ui/beat.ts`); the buttons below are just a manual entry point for tuning.' ],
  'debug.hand.opsPanel': [ 'Actions' ],
  'debug.hand.barPulse': [ '▸ Bar pulse' ],
  'debug.hand.reset': [ 'Reset position' ],
  'debug.hand.selectAll': [ 'Select all' ],
  'debug.hand.clearSelection': [ 'Clear selection' ],
  'debug.hand.export': [ 'Export parameters' ],
  'debug.hand.hint': [ 'Controls: **click** to select, click again to deselect (multi-select works); drag a selected card = move the whole group together; drag left/right = reorder (neighbors make way live); release above the line over the hand = **play**. Click empty space = clear selection. ⚠️ Angle parameters are always **degrees**, lengths always **ratios** (feel.ts documents why at the top).' ],
  'debug.hand.readout.card': [ 'Card ', { type: 'param', name: 'w' }, '×', { type: 'param', name: 'h' }, ' (basis for length ratios)' ],
  'debug.hand.readout.layout': [ 'Layout spacing ', { type: 'param', name: 'spread' }, ' · bend ', { type: 'param', name: 'droop' }, ' / wanted ', { type: 'param', name: 'wanted' }, ' / roll limit ', { type: 'param', name: 'limit' }, ' card heights' ],
  'debug.hand.readout.center': [ 'Arc center (', { type: 'param', name: 'x' }, ', ', { type: 'param', name: 'y' }, ') · roll gain ', { type: 'param', name: 'gain' } ],
  'debug.hand.readout.order': [ 'Order ', { type: 'param', name: 'order' } ],
  'debug.hand.readout.hover': [ 'hover ', { type: 'param', name: 'hovered' }, ' · held ', { type: 'param', name: 'held' }, ' · selected [', { type: 'param', name: 'selected' }, '] · played [', { type: 'param', name: 'played' }, ']' ],
  'debug.hand.readout.pulse': [ 'Pulse ', { type: 'param', name: 'pulse' } ],
  'debug.hand.readout.poseHint': [ '(Move the mouse over a card)' ],
} as const;

// 静态检查（写在对象字面量之外，避免 TextKey 循环推断）
debug satisfies Record<string, TextSpec>;
export default debug;
